import { httpApi } from '@api/http.api';
import { BASE_PAYLOAD } from '@routes/sidebar/admin-center/___shared___/constants.ts';
import type { CommonTableRequest } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type RankReferenceRequest = CommonTableRequest;

export interface RankReferenceCommon {
    shortCode: string | null;
    tenantName: string;
    tenantShortCode: string;
    proficiencyLevelId: number | null;
    proficiencyLevelName?: string | null;
    vesselDepartmentId: number | null;
    vesselDepartmentName?: string | null;
    promotionRankId: number | null;
    promotionRankName?: string | null;
    supervisingRankId: number | null;
    supervisingRankName?: string | null;
    isActive: boolean;
}

interface RankCommonPart {
    id: number;
    name: string;
}

export interface RankResponse extends RankReferenceCommon {
    id: number;
    name: string;
    createdAtUTC: string;
    itemOrder: number;
}

export interface RankRequest extends RankReferenceCommon {
    id: number | null;
    itemOrder: number;
}

export interface VesselDepartmentResponse extends RankCommonPart {
    shortCode: string;
}

export interface ProficiencyLevelResponse extends RankCommonPart {
    proficiencyLevel: string;
}

export interface RankSetCreateRequest {
    name: string;
}
export interface RankSetResponse extends RankSetCreateRequest {
    id: number;
}

export interface DuplicateRankSetResponse extends RankSetCreateRequest {
    duplicateOfId: number;
}

export interface UpdateRankSetRequest {
    newName: string;
}

export interface RankShortCodeResponse extends RankCommonPart {
    shortCode: string;
}

export const getApiRankReferences = (
    clientId: number,
    tenantAlias: string,
    rankSetId: number,
    payload: RankReferenceRequest,
): Promise<PaginatedResponse<RankResponse, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<RankResponse, EmptyResultsDescription>
        >(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-reference/${rankSetId}/search`, payload)
        .then(({ data }) => data);

export const updateApiRankReferences = (
    clientId: number,
    tenantAlias: string,
    rankSetId: number,
    payload: RankRequest[],
): Promise<PaginatedResponse<RankResponse, EmptyResultsDescription>> =>
    httpApi
        .put<
            PaginatedResponse<RankResponse, EmptyResultsDescription>
        >(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-reference/${rankSetId}`, payload)
        .then(({ data }) => data);

export const createApiRankReferences = (
    clientId: number,
    tenantAlias: string,
    rankSetId: number,
    payload: RankReferenceCommon,
): Promise<PaginatedResponse<RankResponse, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<RankResponse, EmptyResultsDescription>
        >(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-reference/${rankSetId}`, payload)
        .then(({ data }) => data);

export const toggleActiveApiRankReferences = (
    clientId: number,
    tenantAlias: string,
    rankSetId: number,
    rankReferenceId: number,
): Promise<PaginatedResponse<RankResponse, EmptyResultsDescription>> =>
    httpApi
        .put<
            PaginatedResponse<RankResponse, EmptyResultsDescription>
        >(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-reference/${rankSetId}/toggle-active/${rankReferenceId}`)
        .then(({ data }) => data);

export const getApiVesselDepartments = (
    clientId: number,
    tenantAlias: string,
): Promise<PaginatedResponse<VesselDepartmentResponse, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<VesselDepartmentResponse, EmptyResultsDescription>
        >(`admin-center/clients/${clientId}/tenants/${tenantAlias}/vessel-departments/search`, BASE_PAYLOAD)
        .then(({ data }) => data);

export const getApiProficiencyLevels = (
    clientId: number,
    tenantAlias: string,
): Promise<PaginatedResponse<ProficiencyLevelResponse, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<ProficiencyLevelResponse, EmptyResultsDescription>
        >(`admin-center/clients/${clientId}/tenants/${tenantAlias}/proficiency-levels/search`, BASE_PAYLOAD)
        .then(({ data }) => data);

export const getApiRankShortCodes = (clientId: number, tenantAlias: string): Promise<RankShortCodeResponse[]> =>
    httpApi
        .get<RankShortCodeResponse[]>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-short-codes`)
        .then(({ data }) => data);

export const getApiRankSets = (clientId: number, tenantAlias: string): Promise<RankSetResponse[]> =>
    httpApi
        .get<RankSetResponse[]>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-sets`)
        .then(({ data }) => data);

export const createApiRankSet = (
    clientId: number,
    tenantAlias: string,
    payload: RankSetCreateRequest,
): Promise<RankSetResponse[]> =>
    httpApi
        .post<RankSetResponse[]>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-sets`, payload)
        .then(({ data }) => data);

export const updateApiRankSet = (
    clientId: number,
    tenantAlias: string,
    id: number,
    payload: UpdateRankSetRequest,
): Promise<RankSetResponse[]> =>
    httpApi
        .put<RankSetResponse[]>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-sets/${id}`, payload)
        .then(({ data }) => data);

export const deleteApiRankSet = (clientId: number, tenantAlias: string, id: number): Promise<any> =>
    httpApi
        .delete<any>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-sets/${id}`)
        .then(({ data }) => data);

export const duplicateApiRankSet = (
    clientId: number,
    tenantAlias: string,
    payload: DuplicateRankSetResponse,
): Promise<RankSetResponse> =>
    httpApi
        .post<RankSetResponse>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/rank-sets/duplicate`, payload)
        .then(({ data }) => data);
